/**
 * The coinProducts duck. Default export is the reducer
 *
 * Action Types:
 * - COIN_PRODUCTS_REQUESTED
 * - COIN_PRODUCTS_SUCCEEDED
 * - COIN_PRODUCTS_FAILED
 *
 * Action Creators:
 * - coinProductsRequested
 * - coinProductsSucceeded
 * - coinProductsFailed
 * - coinProductsModalClose
 */
import get from 'lodash/get';

import type {
  CoinProductsState,
  CoinProductsAction,
  CoinProductsSucceeded,
  CoinProductsRequest,
  CoinProductsFailed,
  CoinProduct,
} from '../types';

/** @type {ActionType} */
export const COIN_PRODUCTS_REQUESTED =
  'PB/coinProducts/COIN_PRODUCTS_REQUESTED';

/** @type {ActionType} */
export const COIN_PRODUCTS_SUCCEEDED =
  'PB/coinProducts/COIN_PRODUCTS_SUCCEEDED';

/** @type {ActionType} */
export const COIN_PRODUCTS_FAILED = 'PB/coinProducts/COIN_PRODUCTS_FAILED';

export const initialState = {
  coinProducts: [],
  loading: true,
  failed: undefined,
};

/** @ignore */
export default function coinProductsReducer(
  state: CoinProductsState = initialState,
  action: CoinProductsAction,
): CoinProductsState {
  switch (action.type) {
    case COIN_PRODUCTS_REQUESTED:
      return { ...state, loading: true };
    case COIN_PRODUCTS_SUCCEEDED:
      const coinProducts = get(
        action,
        'payload.coinProducts',
        state.coinProducts,
      );
      return { ...state, coinProducts, loading: false, failed: false };
    case COIN_PRODUCTS_FAILED:
      return { ...state, loading: false, failed: true };
    default:
      return state;
  }
}

/**
 * This action creator will return a COIN_PRODUCT_REQUESTED action
 *
 * @return {Action}
 * @property {string} type COIN_PRODUCTS_REQUESTED
 */
export function coinProductsRequested(
  currency: string,
  preferred_currency: string,
): CoinProductsRequested {
  return {
    type: COIN_PRODUCTS_REQUESTED,
    payload: {
      currency,
      preferred_currency,
    },
  };
}

/**
 * This action creator will return a COIN_PRODUCT_SUCCEEDED action given
 * a parsed array of coinProducts.
 *
 * @param {Array} CoinProduct[] - array of coin products
 * @return {Action}
 * @property {string} type COIN_PRODUCTS_SUCCEEDED
 * @property {Object} payload
 */
export function coinProductsSucceeded(
  coinProducts: CoinProduct[],
): CoinProductsSucceeded {
  return {
    type: COIN_PRODUCTS_SUCCEEDED,
    payload: { coinProducts },
  };
}

/**
 * This action creator will return a COIN_PRODUCTS_FAILED action given an error
 * message.
 *
 * @param {string} message - A message summarizing the error. May be shown to
 *     the user (So watch yr language).
 * @return {Action}
 * @property {string} type COIN_PRODUCTS_FAILED
 * @property {Object} payload The payload is an object containing the message
 *     e.g.`{ message: '' }`
 */
export function coinProductsFailed(message: string): CoinProductsFailed {
  return {
    type: COIN_PRODUCTS_FAILED,
    payload: { message },
  };
}
